import * as React from 'react'
import {Dialog, DialogDescription, DialogDismiss, DialogHeading} from '@ariakit/react'
import {faTimes} from '@fortawesome/free-solid-svg-icons'
import noop from 'lodash/noop'
import {fromMaybe} from '@freckle/maybe'
import {addMaybeClassName} from '../../helpers/classnames'
import {useSafeTranslation} from '../../helpers/translate'
import type Button from './../button'
import type {ButtonAsync} from './../button-async'
import {ButtonCircular} from './../button-circular'
import {ButtonSpacer} from './../button-spacer'
import type Link from './../link'
import {ReadAloud} from './../read-aloud'
import {Text} from './../typography'
import {
  modalHeader,
  modalBody,
  modalFooter,
  titleStyle,
  subTitleStyle,
  singleButton,
  spacerStyle,
  closeButtonStyle,
  dialog,
  lg,
  backdrop
} from './modal.module.scss'

type ModalSizeT = 'sm' | 'lg'

type Props = {
  show: boolean
  title: React.ReactNode
  subTitle?: React.ReactNode
  children: React.ReactNode
  size?: ModalSizeT
  ctaButtons?: Array<React.ReactElement<typeof Button | typeof ButtonAsync | typeof Link>>
  onHide: () => void
  modalDataTest?: string | null
  closeModalDataTest?: string | null
  audioUrl?: string | null
  onAudioFailure?: () => void
  fixedBody?: boolean | null
}

export const Modal = (props: Props) => {
  const {
    show,
    onHide,
    title,
    subTitle,
    audioUrl,
    onAudioFailure,
    modalDataTest,
    closeModalDataTest,
    fixedBody,
    children,
    ctaButtons,
    size
  } = props

  const {t} = useSafeTranslation()

  return (
    <Dialog
      open={show}
      onClose={onHide}
      backdrop={<div className={backdrop} />}
      data-test={fromMaybe(() => 'modal', modalDataTest)}
      preventBodyScroll={fixedBody === true}
      className={addMaybeClassName(dialog, size === 'lg' ? lg : null)}
      hideOnInteractOutside={false}
      // Preserve existing behaviour of unmounting the modal when it is hidden
      // If not done, tests can find multiple elements (eg. modal close button) with the same data-test attribute and fail as the one it found might not be visible.
      unmountOnHide
    >
      <DialogDismiss render={<div className={closeButtonStyle} />}>
        <ButtonCircular
          icon={{tag: 'icon', icon: faTimes}}
          title={{tag: 'without-translation', str: t('MODAL_CLOSE')}}
          size="xl"
          style="danger"
          onClick={noop}
          data-test={fromMaybe(() => 'close-modal-button', closeModalDataTest)}
        />
      </DialogDismiss>
      <DialogHeading className={modalHeader}>
        {audioUrl !== null && audioUrl !== undefined ? (
          <ReadAloud
            audioFilePath={audioUrl}
            style="primary"
            onAudioFailure={fromMaybe(() => noop, onAudioFailure)}
          />
        ) : null}

        <Text style="heading-2" htmlElement="div" addClass={titleStyle}>
          {title}
        </Text>

        {subTitle !== null && subTitle !== undefined ? (
          <Text style="body-1" addClass={subTitleStyle} htmlElement="div">
            {subTitle}
          </Text>
        ) : null}
      </DialogHeading>
      <DialogDescription render={<div className={modalBody} />}>{children}</DialogDescription>
      {ctaButtons !== null && ctaButtons !== undefined && ctaButtons.length > 0 ? (
        <div className={modalFooter}>
          {ctaButtons.length === 1 ? (
            <div className={singleButton}>{ctaButtons}</div>
          ) : (
            <ButtonSpacer addClass={spacerStyle} spacingSize="xxs">
              {ctaButtons}
            </ButtonSpacer>
          )}
        </div>
      ) : null}
    </Dialog>
  )
}
